import { useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { recordSecurityStrike, STRIKE_REASONS } from '../utils/studentSecurity';

/**
 * Deter copy/screenshot on student portal pages.
 * After repeated violations the account is blocked and admin is notified.
 */
export function useContentProtection(enabled = true) {
  const { currentUser, isAdmin } = useAuth();

  const report = useCallback(
    async (reason) => {
      if (!enabled || !currentUser || isAdmin) return;
      const result = await recordSecurityStrike(currentUser.uid, reason);
      if (result.blocked) {
        window.location.href = '/portal-blocked';
      }
    },
    [enabled, currentUser, isAdmin]
  );

  useEffect(() => {
    if (!enabled || !currentUser || isAdmin) return undefined;

    const root = document.documentElement;
    root.classList.add('sba-protected-content');

    function onCopy(e) {
      e.preventDefault();
      void report(STRIKE_REASONS.copy);
    }

    function onCut(e) {
      e.preventDefault();
      void report(STRIKE_REASONS.copy);
    }

    function onContextMenu(e) {
      e.preventDefault();
    }

    function onKeyDown(e) {
      const key = e.key?.toLowerCase();
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (key === 'c' || key === 'x' || key === 'a' || key === 'p' || key === 's')) {
        e.preventDefault();
        if (key === 'c' || key === 'x') void report(STRIKE_REASONS.copy);
        if (key === 'p') void report(STRIKE_REASONS.screenshot);
      }
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        void report(STRIKE_REASONS.screenshot);
      }
    }

    function onVisibility() {
      if (document.hidden) {
        /* tab switch during protected session — soft signal only after repeated use */
      }
    }

    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCut);
    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      root.classList.remove('sba-protected-content');
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('cut', onCut);
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, currentUser, isAdmin, report]);
}
